import React, { useEffect } from 'react';
import { useWorldStore } from '../state/worldStore';
import { edgeInset } from '../config';

// CallRing — somebody is calling you.
//
// Khari, 2026-08-04: "they get the notification with a link to answer the call."
//
// TWO DOORS INTO THE SAME RING, because the two cases are genuinely different:
//
//   in the world   the `/world` socket delivers `incoming_call` the instant it
//                  is placed, and this banner appears over whatever you are
//                  doing. No refresh, no notification centre.
//   anywhere else  the notification (and its push) carries a link into the
//                  world with `?call=CODE`, which lands here as the same ring.
//
// It deliberately does NOT open the phone by itself. A call that seizes the
// screen is a call that interrupts a scene, a conversation, a session; the ring
// asks, and answering is the thing that takes over.
export default function CallRing() {
  const videoRing = useWorldStore((s) => s.videoRing);
  const ringVideo = useWorldStore((s) => s.ringVideo);
  const dismissVideoRing = useWorldStore((s) => s.dismissVideoRing);
  const openVideoCall = useWorldStore((s) => s.openVideoCall);
  const videoCall = useWorldStore((s) => s.videoCall);

  // A link followed from a notification. Read once, then STRIPPED from the URL:
  // a code left in the address bar re-rings on every reload, long after the
  // call it belonged to has gone.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const q = new URLSearchParams(window.location.search);
    const code = q.get('call');
    if (!code) return;
    ringVideo({ code: code.toUpperCase(), from: null, asAvatarId: q.get('as') || null, at: Date.now() });
    q.delete('call');
    const rest = q.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`);
  }, [ringVideo]);

  if (!videoRing || videoCall) return null;   // already answering — the phone owns it

  const who = videoRing.from?.name || 'Someone';

  return (
    <div style={{
      position: 'fixed', top: 'max(16px, env(safe-area-inset-top))', right: edgeInset(16, 'right'),
      zIndex: 90, width: 'min(300px, calc(100vw - 32px))', pointerEvents: 'auto',
      background: 'rgba(8,12,9,0.95)', border: '1px solid rgba(125,255,157,0.45)', borderRadius: 16,
      padding: '12px 14px', color: '#eef7ea', fontFamily: 'system-ui, sans-serif',
      boxShadow: '0 18px 50px rgba(0,0,0,0.55)', animation: 'ht-call-pulse 1.5s ease-in-out infinite',
    }}>
      {/* Its OWN keyframes: `ht-ring` lives inside Phone.jsx and only exists
          while the phone is mounted — and it is a SHAKE, authored for a handset.
          A banner that rotates reads as a bug. */}
      <style>{`
        @keyframes ht-call-pulse {
          0%, 100% { box-shadow: 0 18px 50px rgba(0,0,0,0.55), 0 0 0 0 rgba(125,255,157,0.35); }
          50%      { box-shadow: 0 18px 50px rgba(0,0,0,0.55), 0 0 0 7px rgba(125,255,157,0); }
        }
        @media (prefers-reduced-motion: reduce) { @keyframes ht-call-pulse { 0%,100% { box-shadow: 0 18px 50px rgba(0,0,0,0.55); } } }
      `}</style>
      <div style={{ font: '600 10px system-ui', letterSpacing: 1.2, textTransform: 'uppercase', color: '#7dff9d', opacity: 0.85 }}>
        Incoming call
      </div>
      <div style={{ font: '700 15px system-ui', marginTop: 3 }}>{who}</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button
          onClick={() => openVideoCall({ code: videoRing.code, peerName: videoRing.from?.name || null, answering: true })}
          style={{
            flex: 1, cursor: 'pointer', border: 'none', borderRadius: 10, padding: '9px 0',
            font: '800 13px system-ui', background: '#2fd06a', color: '#08120a',
          }}
        >Answer</button>
        <button
          onClick={dismissVideoRing}
          style={{
            cursor: 'pointer', borderRadius: 10, padding: '9px 14px', font: '700 13px system-ui',
            border: '1px solid rgba(255,233,196,0.25)', background: 'transparent', color: '#ffe9c4',
          }}
        >Ignore</button>
      </div>
    </div>
  );
}
